/**
 * Pure view helpers for the Client Tracking page.
 *
 * Kept out of the component so the sorting, CSV shaping and sparkline
 * geometry can be unit tested without rendering React.
 */

/** Column key -> value accessor used by both sorting and the CSV export. */
export const COLUMNS = {
  name: (client) => client.organizationName || "",
  riskScore: (client) => client.riskScore ?? 0,
  seats: (client) => client.seats || 0,
  activeUsers: (client) => client.activeUsers || 0,
  // Left nullable on purpose: an org with no member roster has unknown
  // adoption, which must sink rather than sort as the worst 0%.
  adoptionRate: (client) => client.adoptionRate,
  accesses: (client) => client.accesses || 0,
  accessesPerUser: (client) => client.accessesPerUser || 0,
  manualRuns: (client) => client.manualRuns || 0,
  trainingInstructions: (client) => client.trainingInstructions || 0,
  events: (client) => client.events || 0,
  activeDays: (client) => client.activeDays || 0,
  lastActiveAt: (client) => client.lastActiveAt || 0,
  growth: (client) => client.deltas?.accesses,
};

/**
 * Sorts clients by a column.
 *
 * Growth and adoption are the nullable columns: a client with no prior-period baseline
 * has `null` growth, which is not "worse than -100%" — it is unknown. Those
 * rows always sink to the bottom regardless of direction so they never crowd
 * out the real movers the sort was meant to surface.
 *
 * @param {object[]} clients
 * @param {string} key one of `COLUMNS`
 * @param {"asc"|"desc"} direction
 * @returns {object[]} a new array; the input is never mutated
 */
export function sortClients(clients, key, direction = "desc") {
  const accessor = COLUMNS[key] || COLUMNS.events;
  const sign = direction === "asc" ? 1 : -1;

  return [...(clients || [])].sort((a, b) => {
    const left = accessor(a);
    const right = accessor(b);

    const leftMissing = left === null || left === undefined;
    const rightMissing = right === null || right === undefined;
    if (leftMissing && rightMissing) return 0;
    if (leftMissing) return 1;
    if (rightMissing) return -1;

    if (typeof left === "string" || typeof right === "string") {
      return sign * String(left).localeCompare(String(right));
    }
    return sign * (left - right);
  });
}

/** Locale-formatted integer, with a dash for missing values. */
export function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString();
}

/** Signed percentage, or "New" when there was no baseline to compare against. */
export function formatChange(pct) {
  if (pct === null || pct === undefined) return "New";
  if (pct === 0) return "0%";
  return `${pct > 0 ? "+" : ""}${pct}%`;
}

/** "up" | "down" | "flat" | "new" — drives the arrow and the colour. */
export function changeDirection(pct) {
  if (pct === null || pct === undefined) return "new";
  if (pct > 0) return "up";
  if (pct < 0) return "down";
  return "flat";
}

/** Compact "3d ago" style age for a ms timestamp. */
export function formatRelative(ms, now = Date.now()) {
  if (!ms) return "Never";
  const diff = now - ms;
  if (diff < 60_000) return "just now";
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return "—";
  }
}

/** "Aug 20" style short label for a "YYYY-MM-DD" day key. */
export function formatDayLabel(day) {
  if (!day) return "";
  const ms = Date.parse(`${day}T00:00:00.000Z`);
  if (!Number.isFinite(ms)) return day;
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** "Aug 17 – Aug 23" label for a week bucket. */
export function formatWeekLabel(week) {
  if (!week?.weekStart) return "";
  return `${formatDayLabel(week.weekStart)} – ${formatDayLabel(week.weekEnd)}`;
}

/**
 * SVG polyline points for an inline sparkline.
 *
 * A flat series is drawn along the vertical middle rather than pinned to the
 * baseline, so "steady at 40 accesses a day" does not look identical to "no
 * activity at all".
 *
 * @param {number[]} values
 * @param {{ width?: number, height?: number, padding?: number }} [options]
 * @returns {string} `points` attribute value, empty when there is nothing to draw
 */
export function sparklinePoints(values, { width = 96, height = 26, padding = 2 } = {}) {
  const series = (values || []).map((v) => Number(v) || 0);
  if (series.length === 0) return "";

  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;
  const max = Math.max(...series);
  const min = Math.min(...series);
  const span = max - min;

  if (series.length === 1) {
    return `${padding},${padding + usableHeight / 2} ${width - padding},${padding + usableHeight / 2}`;
  }

  const step = usableWidth / (series.length - 1);

  return series
    .map((value, index) => {
      const x = padding + index * step;
      const ratio = span === 0 ? 0.5 : (value - min) / span;
      const y = padding + usableHeight - ratio * usableHeight;
      return `${Math.round(x * 100) / 100},${Math.round(y * 100) / 100}`;
    })
    .join(" ");
}

/**
 * 0..1 intensity for the activity heat cells, scaled against the busiest day
 * in the same row so a small client is still legible next to a large one.
 */
export function heatIntensity(value, max) {
  const numeric = Number(value) || 0;
  if (numeric <= 0 || !max) return 0;
  return Math.min(1, numeric / max);
}

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvFrom = (header, rows) =>
  [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");

/** One row per client — the table as exported. */
export function toSummaryCsv(clients) {
  return csvFrom(
    [
      "Organization",
      "Organization ID",
      "Seats",
      "Active users",
      "Adoption %",
      "Accesses",
      "Accesses / user",
      "Manual runs",
      "Training instructions",
      "Training instructions (all time)",
      "Events",
      "Active days",
      "Loans touched",
      "Accesses change %",
      "Manual runs change %",
      "Last active",
    ],
    (clients || []).map((client) => [
      client.organizationName,
      client.organizationId,
      client.seats,
      client.activeUsers,
      client.adoptionRate,
      client.accesses,
      client.accessesPerUser,
      client.manualRuns,
      client.trainingInstructions,
      client.trainingInstructionsAllTime,
      client.events,
      client.activeDays,
      client.loansTouched,
      client.deltas?.accesses ?? "",
      client.deltas?.manualRuns ?? "",
      client.lastActiveAt ? new Date(client.lastActiveAt).toISOString() : "",
    ]),
  );
}

/** One row per client per day — the evolution series as exported. */
export function toDailyCsv(clients) {
  const rows = [];
  for (const client of clients || []) {
    for (const point of client.daily || []) {
      rows.push([
        client.organizationName,
        client.organizationId,
        point.day,
        point.activeUsers,
        point.accesses,
        point.manualRuns,
        point.trainingInstructions,
        point.events,
        point.deltaAccesses ?? "",
        point.deltaEvents ?? "",
      ]);
    }
  }

  return csvFrom(
    [
      "Organization",
      "Organization ID",
      "Day",
      "Active users",
      "Accesses",
      "Manual runs",
      "Training instructions",
      "Events",
      "Accesses vs previous day",
      "Events vs previous day",
    ],
    rows,
  );
}

/** Triggers a client-side download of `content` as `filename`. */
export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
